import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Edit as EditIcon, Send as SendIcon, Search as SearchIcon, DeleteOutline as DeleteOutlineIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useAdminContext } from '../AdminContext';
import { extractTextWithoutEmojis, extractEmojisFromFlairText } from '../utils/flairTextUtils';

type DeprecatedRow = {
  id: number;
  sub_name: string;
  flair_code_old: string;
  flair_code: string;
  permanent?: number;
};

type ExportFlairOption =
  | { group: 'flair_id'; flair_id: number; label: string }
  | { group: 'flair_name'; flair_name: string; label: string };

function exportFlairOptionValue(o: ExportFlairOption): string {
  if (o.group === 'flair_id') {
    return `id:${o.flair_id}`;
  }
  return `name:${encodeURIComponent(o.flair_name)}`;
}

function parseExportFlairSelection(value: string): { flair_id: number } | { flair_name: string } | null {
  if (!value) return null;
  if (value.startsWith('id:')) {
    const n = parseInt(value.slice(3), 10);
    if (!Number.isFinite(n)) return null;
    return { flair_id: n };
  }
  if (value.startsWith('name:')) {
    try {
      return { flair_name: decodeURIComponent(value.slice(5)) };
    } catch {
      return null;
    }
  }
  return null;
}

export default function UserFlairTab() {
  const {
    user,
    subreddit,
    setMessage,
    handleGetUserFlairs,
    isGettingUserFlairs,
    userFlairSyncProgress,
    handleValidateUserFlairs,
    isValidating,
    validationResults,
    setValidationResults,
    fixQueueStatus,
    enqueueProgress,
    loadFixQueueStatus,
    handleDrainFixes,
    handleFixQueuePause,
    isDrainingFixes,
    userFlairSearch,
    setUserFlairSearch,
    setUserFlairPage,
    isLoadingUserFlairs,
    userFlairs,
    editingUserFlair,
    setEditingUserFlair,
    handleUpdateUserFlair,
    handlePushUserFlairToReddit,
    isPushingFlair,
    userFlairPage,
    userFlairTotalPages,
    userFlairTotal,
  } = useAdminContext();

  useEffect(() => {
    loadFixQueueStatus?.();
  }, [subreddit, user]);

  const [deprecatedRows, setDeprecatedRows] = useState<DeprecatedRow[]>([]);
  const [deprecatedLoading, setDeprecatedLoading] = useState(false);
  const [depOld, setDepOld] = useState('');
  const [depNew, setDepNew] = useState('');
  const [depPermanent, setDepPermanent] = useState(false);
  const [depSaving, setDepSaving] = useState(false);

  const [exportFlairOptions, setExportFlairOptions] = useState<ExportFlairOption[]>([]);
  const [exportFlairLoading, setExportFlairLoading] = useState(false);
  const [exportSelectedCode, setExportSelectedCode] = useState('');
  const [exportDownloading, setExportDownloading] = useState(false);

  const loadExportFlairOptions = useCallback(async () => {
    if (!subreddit || !user) return;
    setExportFlairLoading(true);
    try {
      const { API_BASE_URL } = await import('../../../config');
      const response = await fetch(
        `${API_BASE_URL}/moderator/${subreddit}/export-user-flairs-for-flair/flairs`,
        { headers: { Authorization: `Bearer ${user.username}` } }
      );
      const data = await response.json();
      if (response.ok && Array.isArray(data.flairs)) {
        setExportFlairOptions(
          data.flairs.map(
            (r: { group: string; flair_id?: number; flair_name?: string; label: string }) =>
              r.group === 'flair_name'
                ? { group: 'flair_name' as const, flair_name: String(r.flair_name || ''), label: r.label }
                : { group: 'flair_id' as const, flair_id: Number(r.flair_id), label: r.label }
          )
        );
      } else {
        setMessage?.({
          type: 'error',
          text: data.error || 'Failed to load flair list for export',
          details: data.details,
        });
      }
    } catch (e) {
      setMessage?.({
        type: 'error',
        text: e instanceof Error ? e.message : 'Failed to load flair list for export',
      });
    } finally {
      setExportFlairLoading(false);
    }
  }, [subreddit, user, setMessage]);

  useEffect(() => {
    void loadExportFlairOptions();
  }, [loadExportFlairOptions]);

  const handleExportUsersByFlair = async () => {
    if (!subreddit || !user || !exportSelectedCode) {
      setMessage?.({ type: 'warning', text: 'Select a flair first.' });
      return;
    }
    const parsed = parseExportFlairSelection(exportSelectedCode);
    if (!parsed) {
      setMessage?.({ type: 'warning', text: 'Invalid flair selection.' });
      return;
    }
    setExportDownloading(true);
    try {
      const { API_BASE_URL } = await import('../../../config');
      const q =
        'flair_id' in parsed
          ? new URLSearchParams({ flair_id: String(parsed.flair_id) })
          : new URLSearchParams({ flair_name: parsed.flair_name });
      const response = await fetch(
        `${API_BASE_URL}/moderator/${subreddit}/export-user-flairs-for-flair?${q}`,
        { headers: { Authorization: `Bearer ${user.username}` } }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setMessage?.({
          type: 'error',
          text: data.error || 'Export failed',
          details: data.details,
        });
        return;
      }
      const blob = await response.blob();
      let filename =
        'flair_id' in parsed
          ? `${subreddit}_users_flair_${parsed.flair_id}.csv`
          : `${subreddit}_users_${parsed.flair_name.slice(0, 40)}.csv`;
      const cd = response.headers.get('Content-Disposition');
      const m = /filename="([^"]+)"/.exec(cd || '');
      if (m) filename = m[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setMessage?.({ type: 'success', text: `Downloaded ${filename}` });
    } catch (e) {
      setMessage?.({
        type: 'error',
        text: e instanceof Error ? e.message : 'Export failed',
      });
    } finally {
      setExportDownloading(false);
    }
  };

  const loadDeprecated = useCallback(async () => {
    if (!subreddit || !user) return;
    setDeprecatedLoading(true);
    try {
      const { API_BASE_URL } = await import('../../../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/deprecated-flaircodes`, {
        headers: { Authorization: `Bearer ${user.username}` },
      });
      const data = await response.json();
      if (response.ok && data.rows) {
        setDeprecatedRows(data.rows);
      } else {
        setMessage?.({
          type: 'error',
          text: data.error || 'Failed to load deprecated flair codes',
          details: data.details,
        });
      }
    } catch (e) {
      setMessage?.({
        type: 'error',
        text: e instanceof Error ? e.message : 'Failed to load deprecated flair codes',
      });
    } finally {
      setDeprecatedLoading(false);
    }
  }, [subreddit, user, setMessage]);

  useEffect(() => {
    void loadDeprecated();
  }, [loadDeprecated]);

  const handleAddDeprecated = async () => {
    if (!subreddit || !user) return;
    const o = depOld.trim();
    const n = depNew.trim();
    if (!o || !n) {
      setMessage?.({ type: 'warning', text: 'Enter both old and new flair codes.' });
      return;
    }
    setDepSaving(true);
    try {
      const { API_BASE_URL } = await import('../../../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/deprecated-flaircodes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.username}`,
        },
        body: JSON.stringify({ flair_code_old: o, flair_code: n, permanent: depPermanent ? 1 : 0 }),
      });
      const data = await response.json();
      if (response.ok) {
        setDepOld('');
        setDepNew('');
        setDepPermanent(false);
        setMessage?.({ type: 'success', text: `Remap added: ${o} → ${n}${depPermanent ? ' (permanent rename)' : ''}` });
        await loadDeprecated();
      } else if (response.status === 409) {
        setMessage?.({ type: 'warning', text: data.details || 'That old code already exists for this sub.' });
      } else {
        setMessage?.({ type: 'error', text: data.error || 'Failed to add remap', details: data.details });
      }
    } catch (e) {
      setMessage?.({
        type: 'error',
        text: e instanceof Error ? e.message : 'Failed to add remap',
      });
    } finally {
      setDepSaving(false);
    }
  };

  const handleDeleteDeprecated = async (id: number) => {
    if (!subreddit || !user) return;
    setDepSaving(true);
    try {
      const { API_BASE_URL } = await import('../../../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/deprecated-flaircodes/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${user.username}` },
      });
      const data = await response.json();
      if (response.ok) {
        setMessage?.({ type: 'success', text: 'Deprecated remap removed.' });
        await loadDeprecated();
      } else {
        setMessage?.({ type: 'error', text: data.error || 'Failed to delete', details: data.details });
      }
    } catch (e) {
      setMessage?.({
        type: 'error',
        text: e instanceof Error ? e.message : 'Failed to delete',
      });
    } finally {
      setDepSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>User Flair Management</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Manage user flairs, validate sync status, and push updates to Reddit.</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button variant="contained" color="primary" onClick={handleGetUserFlairs} disabled={isGettingUserFlairs || !user}>
          {isGettingUserFlairs ? 'Fetching user flairs...' : 'Get User Flair'}
        </Button>
        {isGettingUserFlairs && (
          <Box sx={{ width: '100%', maxWidth: 480, mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {userFlairSyncProgress?.progress?.phase === 'fetch' &&
                `Fetching from Reddit: ${userFlairSyncProgress.progress.fetched ?? '…'} flairs (batch ${userFlairSyncProgress.progress.batch ?? '…'})`}
              {userFlairSyncProgress?.progress?.phase === 'db' &&
                `Writing database: ${userFlairSyncProgress.progress.written ?? 0} / ${userFlairSyncProgress.progress.total ?? '…'} rows`}
              {userFlairSyncProgress?.progress?.phase === 'done' && 'Finishing…'}
              {userFlairSyncProgress?.db_user_flair_rows != null &&
                ` · user_flair rows in DB (approx.): ${userFlairSyncProgress.db_user_flair_rows.toLocaleString()}`}
            </Typography>
          </Box>
        )}
        <Button variant="outlined" color="warning" onClick={() => handleValidateUserFlairs(false, false)} disabled={isValidating || !user}>
          {isValidating ? 'Validating...' : 'Validate Flairs'}
        </Button>
        <Button variant="contained" color="success" onClick={() => handleValidateUserFlairs(true, true, 'robust')} disabled={isValidating || !user} title="Scan in pages, write DB fixes, enqueue Reddit pushes, then drain">
          {isValidating ? 'Queuing...' : 'Fix All Auto-Fixable Issues'}
        </Button>
        {validationResults && ((validationResults.issues && validationResults.issues.length > 0) || validationResults.users_with_issues > 0) && (
          <>
            <Button variant="outlined" color="info" onClick={() => handleValidateUserFlairs(true, false, 'gentle')} disabled={isValidating || !user} title="Fix flair text only when it matches flair names/abbreviations (skip custom mod text)">
              {isValidating ? 'Queuing...' : 'Fix Flair Text (Gentle)'}
            </Button>
            <Button variant="outlined" color="info" onClick={() => handleValidateUserFlairs(true, false, 'robust')} disabled={isValidating || !user} title="Force rebuild all flair text from flair1/flair2 (overwrites custom text)">
              {isValidating ? 'Queuing...' : 'Fix Flair Text (Robust)'}
            </Button>
            <Button variant="outlined" color="info" onClick={() => handleValidateUserFlairs(false, true)} disabled={isValidating || !user} title="Fix flair CSS mismatches">
              {isValidating ? 'Queuing...' : 'Fix Flair CSS Mismatch'}
            </Button>
          </>
        )}
      </Box>

      {(isValidating || enqueueProgress) && enqueueProgress && (
        <Box sx={{ width: '100%', maxWidth: 520, mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            Scanning {enqueueProgress.scanned?.toLocaleString?.() ?? enqueueProgress.scanned ?? 0}
            {enqueueProgress.enqueued != null && ` · queued ${enqueueProgress.enqueued}`}
            {enqueueProgress.auto_fixed != null && ` · DB updates ${enqueueProgress.auto_fixed}`}
          </Typography>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="subtitle1">Reddit fix queue</Typography>
            <Typography variant="body2" color="text.secondary">
              Pending {fixQueueStatus?.counts?.pending ?? 0}
              {fixQueueStatus?.counts?.draining ? ` · draining ${fixQueueStatus.counts.draining}` : ''}
              {fixQueueStatus?.counts?.error ? ` · errors ${fixQueueStatus.counts.error}` : ''}
              {fixQueueStatus?.counts?.done ? ` · done ${fixQueueStatus.counts.done}` : ''}
              {` · ${fixQueueStatus?.batches_this_hour ?? 0}/${fixQueueStatus?.max_batches_per_hour ?? 120} batches this hour`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!fixQueueStatus?.paused}
                  onChange={(e) => handleFixQueuePause(e.target.checked)}
                  disabled={!user}
                />
              }
              label="Pause drain"
            />
            <Button variant="outlined" onClick={() => loadFixQueueStatus?.()} disabled={!user}>Refresh</Button>
            <Button variant="contained" onClick={() => handleDrainFixes(true)} disabled={isDrainingFixes || !user || !!fixQueueStatus?.paused}>
              {isDrainingFixes ? 'Draining...' : 'Drain now'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {validationResults && (
        <Box sx={{ mb: 3 }}>
          <Alert severity={validationResults.users_with_issues > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
            Found {validationResults.users_with_issues} users with issues out of {validationResults.total_users} total.
            {validationResults.auto_fixed > 0 && ` ${validationResults.auto_fixed} written to the database.`}
            {validationResults.enqueued > 0 && ` ${validationResults.enqueued} queued for Reddit.`}
            {validationResults.users_with_issues > 0 && ' See table below for details.'}
          </Alert>
          {validationResults.unable_to_fix && validationResults.unable_to_fix.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>{validationResults.unable_to_fix.length} users need manual review:</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Username</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Issues</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationResults.unable_to_fix.map((item: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell>{item.username}</TableCell>
                        <TableCell>{item.reason}</TableCell>
                        <TableCell>
                          {item.issues && item.issues.map((i: any, iidx: number) => (
                            <Chip key={iidx} label={i.type.replace(/_/g, ' ')} color="error" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by username, flair text, or CSS..."
          value={userFlairSearch}
          onChange={(e) => { setUserFlairSearch(e.target.value); setUserFlairPage(1); }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
        />
      </Box>

      {isLoadingUserFlairs ? (
        <CircularProgress />
      ) : (
        <Box>
          <TableContainer sx={{ maxHeight: 600, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Flair1</TableCell>
                  <TableCell>Flair2</TableCell>
                  <TableCell>Flair Text</TableCell>
                  <TableCell>Flair CSS</TableCell>
                  <TableCell>Protect Text</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userFlairs.map((uf: any) => {
                  const hasIssues = validationResults?.issues?.some((i: any) => i.username === uf.username);
                  const isEditing = editingUserFlair?.id === uf.id;
                  return (
                    <TableRow key={uf.id} sx={{ backgroundColor: hasIssues ? 'error.light' : 'inherit' }}>
                      <TableCell>{uf.username}</TableCell>
                      <TableCell>
                        {isEditing ? (
                          <TextField size="small" value={editingUserFlair.flair1 || ''} onChange={(e) => setEditingUserFlair({ ...editingUserFlair, flair1: e.target.value })} sx={{ width: 150 }} />
                        ) : (uf.flair1 || '-')}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <TextField size="small" value={editingUserFlair.flair2 || ''} onChange={(e) => setEditingUserFlair({ ...editingUserFlair, flair2: e.target.value })} sx={{ width: 150 }} />
                        ) : (uf.flair2 || '-')}
                      </TableCell>
                      <TableCell>
                        {isEditing && editingUserFlair.protect_text === 1 ? (
                          <TextField
                            size="small"
                            value={extractTextWithoutEmojis(editingUserFlair.flair_text || '')}
                            onChange={(e) => {
                              const textWithoutEmojis = e.target.value;
                              const emojis = extractEmojisFromFlairText(editingUserFlair.flair_text || '');
                              const fullText = emojis + (textWithoutEmojis ? ' ' + textWithoutEmojis : '');
                              if (fullText.length <= 64) setEditingUserFlair({ ...editingUserFlair, flair_text: fullText });
                            }}
                            inputProps={{ maxLength: 64 }}
                            helperText={`${extractTextWithoutEmojis(editingUserFlair.flair_text || '').length}/64`}
                            error={((editingUserFlair.flair_text || '').length) > 64}
                            sx={{ width: 300 }}
                          />
                        ) : isEditing ? (
                          <TextField size="small" value={editingUserFlair.flair_text || ''} onChange={(e) => e.target.value.length <= 64 && setEditingUserFlair({ ...editingUserFlair, flair_text: e.target.value })} inputProps={{ maxLength: 64 }} sx={{ width: 300 }} />
                        ) : (
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{uf.flair_text || '-'}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{uf.flair_css || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Checkbox checked={editingUserFlair.protect_text === 1} onChange={(e) => setEditingUserFlair({ ...editingUserFlair, protect_text: e.target.checked ? 1 : 0, flair_text: e.target.checked ? editingUserFlair.flair_text : undefined })} />
                        ) : (
                          <Checkbox checked={uf.protect_text === 1} disabled />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button size="small" variant="contained" onClick={() => {
                              const flairTextToSend = editingUserFlair.protect_text === 1 ? extractTextWithoutEmojis(editingUserFlair.flair_text || '') : editingUserFlair.flair_text;
                              handleUpdateUserFlair(uf.username, { flair1: editingUserFlair.flair1, flair2: editingUserFlair.flair2, protect_text: editingUserFlair.protect_text, flair_text: flairTextToSend });
                            }}>Save</Button>
                            <Button size="small" onClick={() => setEditingUserFlair(null)}>Cancel</Button>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => setEditingUserFlair({ ...uf })}><EditIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Push to Reddit">
                              <IconButton size="small" onClick={() => handlePushUserFlairToReddit(uf.username)} disabled={isPushingFlair === uf.username}>
                                {isPushingFlair === uf.username ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {userFlairTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
              <Button variant="outlined" onClick={() => setUserFlairPage((p: number) => Math.max(1, p - 1))} disabled={userFlairPage === 1}>Previous</Button>
              <Typography variant="body2">Page {userFlairPage} of {userFlairTotalPages} ({userFlairTotal} total)</Typography>
              <Button variant="outlined" onClick={() => setUserFlairPage((p: number) => Math.min(userFlairTotalPages, p + 1))} disabled={userFlairPage === userFlairTotalPages}>Next</Button>
            </Box>
          )}
        </Box>
      )}

      {validationResults && validationResults.issues && validationResults.issues.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Users with Issues</Typography>
          <TableContainer sx={{ maxHeight: 400, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Flair1</TableCell>
                  <TableCell>Flair2</TableCell>
                  <TableCell>Flair Text</TableCell>
                  <TableCell>Flair CSS</TableCell>
                  <TableCell>Issues</TableCell>
                  <TableCell>Protect Text</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validationResults.issues.map((issue: any) => {
                  const isEditingIssue = editingUserFlair?.username === issue.username;
                  const displayCss = isEditingIssue
                    ? (editingUserFlair.flair_css ?? issue.flair?.flair_css)
                    : issue.flair?.flair_css;
                  const handleProtectTextToggle = async (checked: boolean) => {
                    const flair = issue.flair;
                    if (!flair) return;
                    await handleUpdateUserFlair(issue.username, {
                      flair1: flair.flair1,
                      flair2: flair.flair2,
                      protect_text: checked ? 1 : 0,
                      flair_text: checked ? (flair.flair_text || '') : undefined,
                    });
                    if (setValidationResults && validationResults) {
                      setValidationResults({
                        ...validationResults,
                        issues: validationResults.issues.map((i: any) =>
                          i.username === issue.username
                            ? { ...i, flair: { ...i.flair, protect_text: checked ? 1 : 0 } }
                            : i
                        ),
                      });
                    }
                  };
                  return (
                    <TableRow key={issue.username}>
                      <TableCell>{issue.username}</TableCell>
                      <TableCell>{isEditingIssue ? <TextField size="small" value={editingUserFlair.flair1 || ''} onChange={(e) => setEditingUserFlair({ ...editingUserFlair, flair1: e.target.value })} sx={{ width: 150 }} /> : (issue.flair?.flair1 || '-')}</TableCell>
                      <TableCell>{isEditingIssue ? <TextField size="small" value={editingUserFlair.flair2 || ''} onChange={(e) => setEditingUserFlair({ ...editingUserFlair, flair2: e.target.value })} sx={{ width: 150 }} /> : (issue.flair?.flair2 || '-')}</TableCell>
                      <TableCell>{isEditingIssue ? <TextField size="small" value={editingUserFlair.flair_text || ''} onChange={(e) => e.target.value.length <= 64 && setEditingUserFlair({ ...editingUserFlair, flair_text: e.target.value })} sx={{ width: 300 }} /> : <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{issue.flair?.flair_text || '-'}</Typography>}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayCss || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        {issue.issues && issue.issues.map((i: any, idx: number) => (
                          <Chip key={idx} label={i.type.replace(/_/g, ' ')} color="error" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Protect custom flair text from auto-fix">
                          <Checkbox
                            checked={issue.flair?.protect_text === 1}
                            onChange={(e) => handleProtectTextToggle(e.target.checked)}
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {isEditingIssue ? (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button size="small" variant="contained" onClick={() => handleUpdateUserFlair(issue.username, { flair1: editingUserFlair.flair1, flair2: editingUserFlair.flair2, protect_text: editingUserFlair.protect_text, flair_text: editingUserFlair.protect_text === 1 ? extractTextWithoutEmojis(editingUserFlair.flair_text || '') : editingUserFlair.flair_text })}>Save</Button>
                            <Button size="small" onClick={() => setEditingUserFlair(null)}>Cancel</Button>
                          </Box>
                        ) : (
                          <Button size="small" onClick={() => setEditingUserFlair({ ...issue.flair })}>Edit</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>Deprecated flair codes</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Map old stored flair codes to current codes. Permanent renames apply on Reddit pull and aggregate history under the replacement image.
          Temporary (checkbox off): Reddit pull keeps the old code in DB for trends; validation still offers migrating users to the new code.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            size="small"
            label="Old flair code"
            value={depOld}
            onChange={(e) => setDepOld(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <TextField
            size="small"
            label="New flair code"
            value={depNew}
            onChange={(e) => setDepNew(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={depPermanent}
                onChange={(e) => setDepPermanent(e.target.checked)}
                size="small"
              />
            }
            label="Permanent rename"
          />
          <Button variant="contained" onClick={() => void handleAddDeprecated()} disabled={depSaving || !user}>
            Add remap
          </Button>
          <Button variant="outlined" onClick={() => void loadDeprecated()} disabled={deprecatedLoading || !user}>
            Refresh
          </Button>
        </Box>
        {deprecatedLoading && deprecatedRows.length === 0 ? (
          <CircularProgress size={24} />
        ) : (
          <TableContainer sx={{ maxHeight: 280, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Old code</TableCell>
                  <TableCell>New code</TableCell>
                  <TableCell>Permanent</TableCell>
                  <TableCell align="right" width={56} />
                </TableRow>
              </TableHead>
              <TableBody>
                {deprecatedRows.length === 0 ? (
                    <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">No remaps for this subreddit.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  deprecatedRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.flair_code_old}</Typography></TableCell>
                      <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.flair_code}</Typography></TableCell>
                      <TableCell>{row.permanent === 1 ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove">
                          <span>
                            <IconButton size="small" onClick={() => void handleDeleteDeprecated(row.id)} disabled={depSaving}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>Export CSV with flair</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          One row per logical flair (all images / <code>flair_code</code> variants like <code>texastech</code>,{' '}
          <code>texastech2</code>, … are combined). CSV column <code>username</code> lists anyone whose{' '}
          <code>flair1</code> or <code>flair2</code> matches any of those codes or slot fields, including deprecated remaps.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 280 }} disabled={exportFlairLoading || !user}>
            <InputLabel id="export-flair-select-label">Flair</InputLabel>
            <Select
              labelId="export-flair-select-label"
              label="Flair"
              value={exportSelectedCode}
              onChange={(e) => setExportSelectedCode(e.target.value as string)}
            >
              <MenuItem value="">
                <em>{exportFlairLoading ? 'Loading…' : 'Select flair'}</em>
              </MenuItem>
              {exportFlairOptions.map((o) => (
                <MenuItem key={exportFlairOptionValue(o)} value={exportFlairOptionValue(o)}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => void handleExportUsersByFlair()}
            disabled={exportDownloading || !exportSelectedCode || !user}
          >
            Download CSV
          </Button>
          <Button variant="outlined" onClick={() => void loadExportFlairOptions()} disabled={exportFlairLoading || !user}>
            Refresh list
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
